/**
 * Review Step for CSV Import Wizard
 *
 * Step 3 of the wizard. Shows an analyzing spinner while the backend processes
 * the uploaded data, then transitions directly into the inline review/approve
 * flow — no intermediate "Analysis complete" screen and no separate modal.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import {
  approveCsvImport,
  type ConflictResolutions,
  type CsvImport,
  getCsvImport,
  getFileResults,
  resolveConflicts,
  suggestCoercions,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

import { CoercionReview } from '../review/coercions/CoercionReview';
import { LargeImportCoercionReview } from '../review/coercions/LargeImportCoercionReview';
import {
  hasStoredLargeImportCoercions,
  type ProbeRequestError,
} from '../review/coercions/resumeProbe';
import { ConflictResolution } from '../review/ConflictResolution';
import { ImportProgress } from '../review/ImportProgress';
import { LargeImportSummary } from '../review/LargeImportSummary';
import { computeImportTotals } from '../review/summary/types';
import { SummaryView } from '../review/SummaryView';

// ============================================================================
// Types
// ============================================================================

type ReviewSubStep = 'summary' | 'conflicts' | 'coercions' | 'progress';

interface ReviewStepProps {
  status: string | null;
  importData: CsvImport | null;
  importId: number | null;
  error: string | null;
  isPolling: boolean;
  orgId: number;
  onRetry: () => Promise<void>;
  onEditMappings?: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const POLLING_INTERVAL_MS = 2000;

// ============================================================================
// Inner state components
// ============================================================================

function AnalyzingState() {
  return (
    <div className='flex flex-col items-center justify-center py-12 gap-4'>
      <LoadingIndicator size='sm' label='Analyzing your data…' />
      <p className='text-sm text-tertiary'>This may take a moment</p>
    </div>
  );
}

function AnalysisErrorState({
  error,
  onRetry,
  onError,
}: {
  error: string;
  onRetry: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const handleRetry = useCallback(() => {
    onRetry().catch(err => {
      logger.error(
        'Retry failed',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'ReviewStep' }
      );
      onError("We couldn't reload your import. Try again.");
    });
  }, [onRetry, onError]);

  return (
    <div className='flex flex-col items-center justify-center py-12 gap-4'>
      <p className='text-sm text-utility-error-700 text-center max-w-sm'>
        {error}
      </p>
      <Button color='secondary' size='sm' onClick={handleRetry}>
        Try again
      </Button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ReviewStep({
  status,
  importData: externalImportData,
  importId,
  error: externalError,
  isPolling,
  orgId,
  onRetry,
  onEditMappings,
}: ReviewStepProps) {
  const [subStep, setSubStep] = useState<ReviewSubStep>('summary');
  const [retryError, setRetryError] = useState<string | null>(null);

  // Local import data takes over after approve (post-approve polling)
  const [localImportData, setLocalImportData] = useState<CsvImport | null>(
    null
  );
  const [isApproving, setIsApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [conflictResolutions, setConflictResolutions] =
    useState<ConflictResolutions>({});
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const pollFailureCountRef = useRef(0);
  const isPollInFlightRef = useRef(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [coercionError, setCoercionError] = useState<string | null>(null);

  const importData = localImportData ?? externalImportData;
  const isLargeImport = importData?.analyzed_rows_omitted === true;
  const totals = useMemo(
    () => computeImportTotals(importData?.analysis_result ?? null),
    [importData?.analysis_result]
  );
  const analysisTotal = totals.total;

  const invalidRowCount = useMemo(() => {
    if (isLargeImport) {
      return totals.invalid;
    }
    const fileResults = getFileResults(importData?.analysis_result ?? null);
    let count = 0;
    for (const fileData of Object.values(fileResults)) {
      for (const row of fileData.analyzed_rows ?? []) {
        if (row.action === 'invalid') {
          count += 1;
        }
      }
    }
    return count;
  }, [importData?.analysis_result, isLargeImport, totals.invalid]);

  // True once the agent has run (proposals) or the user has saved edits, so the
  // entry point resumes the review instead of regenerating from scratch. Large
  // imports omit inline rows, so this always starts false — the large coercion
  // view fetches proposals/edits from `/rows` when opened.
  const hasStoredCoercions = useMemo(() => {
    if (isLargeImport) {
      return false;
    }
    const fileResults = getFileResults(importData?.analysis_result ?? null);
    for (const fileData of Object.values(fileResults)) {
      for (const row of fileData.analyzed_rows ?? []) {
        if (row.action !== 'invalid') {
          continue;
        }
        if (
          (row.proposals && Object.keys(row.proposals).length > 0) ||
          (row.coercion_edits && Object.keys(row.coercion_edits).length > 0)
        ) {
          return true;
        }
      }
    }
    return false;
  }, [importData?.analysis_result, isLargeImport]);

  // File-id keys of the analyzed files, used to probe each file's invalid-row
  // working set for stored coercion work on a large import (see the probe in
  // `handleReviewCoercions`). Matches `LargeImportCoercionReview`'s own keys.
  const largeImportFileIds = useMemo(
    () => Object.keys(importData?.analysis_result?.file_summaries ?? {}),
    [importData?.analysis_result]
  );

  // Reset local review state when the import is re-analyzed (status goes back
  // to 'analyzing'). This ensures stale conflict resolutions and approve errors
  // from the previous analysis don't leak into the fresh review.
  useEffect(() => {
    if (status === 'analyzing') {
      setLocalImportData(null);
      setConflictResolutions({});
      setApproveError(null);
      setRetryError(null);
      setSubStep('summary');
    }
  }, [status]);

  // Poll for progress when status is 'importing' (post-approve)
  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Poll while importing (post-approve) or while re-analyzing after an
    // apply-coercions commit (localImportData drives the 'analyzing' case; the
    // initial analysis is polled by the parent useCsvImport hook, not here).
    const shouldPoll =
      importData?.status === 'importing' ||
      (importData?.status === 'analyzing' && localImportData !== null);
    if (!importData || !shouldPoll || !importId) {
      return;
    }

    pollFailureCountRef.current = 0;
    isPollInFlightRef.current = false;
    setPollError(null);

    const pollStatus = async () => {
      // Prevent overlapping requests — if a previous poll is still running, skip this one
      if (isPollInFlightRef.current) {
        return;
      }
      isPollInFlightRef.current = true;

      try {
        const data = await getCsvImport(orgId, importId);
        pollFailureCountRef.current = 0;
        setLocalImportData(data);
        if (data.status !== 'importing' && data.status !== 'analyzing') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      } catch (err) {
        pollFailureCountRef.current += 1;
        logger.error(
          'Failed to poll import status',
          err instanceof Error ? err : new Error(String(err)),
          { import_id: importId, org_id: orgId }
        );
        if (pollFailureCountRef.current >= 3) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          // Body copy only — the "Lost connection…" half is the alert's title.
          setPollError(
            'Your import is still running – please refresh to check its status.'
          );
        }
      } finally {
        isPollInFlightRef.current = false;
      }
    };

    pollingIntervalRef.current = setInterval(pollStatus, POLLING_INTERVAL_MS);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-poll when status changes, not on every importData field update
  }, [importData?.status, orgId, importId]);

  const handleApprove = useCallback(async () => {
    if (!importId) {
      return;
    }
    setIsApproving(true);
    setApproveError(null);
    try {
      if (Object.keys(conflictResolutions).length > 0) {
        await resolveConflicts(orgId, importId, conflictResolutions);
      }
      const data = await approveCsvImport(orgId, importId);
      setLocalImportData(data);
      setSubStep('progress');
    } catch (err) {
      logger.error(
        'Failed to approve import',
        err instanceof Error ? err : new Error(String(err)),
        {
          import_id: importId,
          org_id: orgId,
          has_conflict_resolutions: Object.keys(conflictResolutions).length > 0,
        }
      );
      setApproveError(
        "We couldn't approve this import. Try again, and get in touch if it keeps happening."
      );
    } finally {
      setIsApproving(false);
    }
  }, [orgId, importId, conflictResolutions]);

  const handleResolveConflicts = useCallback(() => {
    setSubStep('conflicts');
  }, []);

  const handleBackToSummary = useCallback(() => {
    setSubStep('summary');
  }, []);

  const handleReviewCoercions = useCallback(async () => {
    if (!importId) {
      return;
    }
    // Small imports: inline rows let `hasStoredCoercions` detect prior work
    // synchronously, so resume without spending another agent run.
    if (hasStoredCoercions) {
      setSubStep('coercions');
      return;
    }
    setIsSuggesting(true);
    setCoercionError(null);
    try {
      // Large imports omit inline `analyzed_rows`, so `hasStoredCoercions` is
      // always false for them and can't see a prior suggestion run. Probe each
      // file's invalid-row working set (the same rows the review loads): if any
      // already carry agent proposals or saved edits, resume the review instead
      // of regenerating (which wastes an agent run). A probe failure is
      // non-fatal — it falls through to a fresh suggestion, so a transient error
      // never blocks fixing rows.
      if (isLargeImport) {
        let alreadySuggested = false;
        try {
          alreadySuggested = await hasStoredLargeImportCoercions(
            orgId,
            importId,
            largeImportFileIds
          );
        } catch (probeErr) {
          const err =
            probeErr instanceof Error ? probeErr : new Error(String(probeErr));
          logger.error('Failed to probe stored coercions; regenerating', err, {
            import_id: importId,
            org_id: orgId,
            operation: 'probe',
            file_id: (err as ProbeRequestError).fileId,
          });
          setCoercionError(
            "Couldn't check for earlier work; generating fresh suggestions."
          );
        }
        if (alreadySuggested) {
          setSubStep('coercions');
          return;
        }
      }
      const updated = await suggestCoercions(orgId, importId);
      setLocalImportData(updated);
      // Fresh suggestions generated successfully — clear any non-fatal probe
      // warning set above so returning to the summary doesn't show a stale
      // error alert for work that ultimately succeeded.
      setCoercionError(null);
      setSubStep('coercions');
    } catch (err) {
      logger.error(
        'Failed to suggest coercions',
        err instanceof Error ? err : new Error(String(err)),
        { import_id: importId, org_id: orgId }
      );
      setCoercionError(
        'Unable to generate suggestions right now. Please try again in a moment.'
      );
    } finally {
      setIsSuggesting(false);
    }
  }, [orgId, importId, hasStoredCoercions, isLargeImport, largeImportFileIds]);

  const handleCoercionsApplied = useCallback((updated: CsvImport) => {
    // The import is re-analyzing (status 'analyzing'); the poll effect will
    // advance it back to 'ready_for_review'. Reset review state for the fresh pass.
    setLocalImportData(updated);
    setConflictResolutions({});
    setSubStep('summary');
  }, []);

  // Still analyzing (but not if polling stopped with an error)
  const isAnalyzing =
    (status === 'analyzing' ||
      status === null ||
      // Re-analyzing after an apply-coercions commit (localImportData drives this).
      importData?.status === 'analyzing' ||
      (isPolling && status !== 'ready_for_review' && status !== 'failed')) &&
    !(externalError && !isPolling);

  if (isAnalyzing) {
    return <AnalyzingState />;
  }

  // Polling error during analysis (status never transitioned to 'failed')
  if (
    externalError &&
    status !== 'ready_for_review' &&
    subStep !== 'progress'
  ) {
    return (
      <AnalysisErrorState
        error={retryError ?? externalError}
        onRetry={onRetry}
        onError={setRetryError}
      />
    );
  }

  // Analysis failed (before approve)
  if (status === 'failed' && subStep !== 'progress') {
    return (
      <AnalysisErrorState
        error={
          retryError ?? externalError ?? 'Analysis failed. Please try again.'
        }
        onRetry={onRetry}
        onError={setRetryError}
      />
    );
  }

  // No data yet (shouldn't happen post-analysis but guard anyway)
  if (!importData) {
    return <AnalyzingState />;
  }

  // Progress sub-step (importing / completed / failed post-approve)
  if (subStep === 'progress') {
    return (
      <>
        {pollError && (
          <div role='alert' className='mb-4'>
            <AlertFloating
              title='Lost connection while tracking import progress'
              description={pollError}
              color='warning'
            />
          </div>
        )}
        <ImportProgress importData={importData} analysisTotal={analysisTotal} />
      </>
    );
  }

  // Approve error banner above summary
  // Use role='alert' for error states requiring immediate announcement
  const approveErrorBanner = approveError ? (
    <div role='alert' className='mb-4'>
      <AlertFloating title='Error' description={approveError} color='error' />
    </div>
  ) : null;

  const coercionEntryBanner =
    invalidRowCount > 0 ? (
      <div className='mb-4'>
        <AlertFloating
          title={`${invalidRowCount} row${invalidRowCount === 1 ? '' : 's'} couldn't be imported.`}
          description={
            hasStoredCoercions
              ? 'Pick up where you left off reviewing the suggested fixes.'
              : 'Let our agent suggest fixes you can review.'
          }
          color='brand'
          confirmLabel={
            hasStoredCoercions
              ? 'Review suggested fixes'
              : 'Let our agent fix these'
          }
          onConfirm={handleReviewCoercions}
          isConfirmLoading={isSuggesting}
          isConfirmDisabled={isSuggesting}
        />
      </div>
    ) : null;

  const coercionErrorBanner = coercionError ? (
    <div role='alert' className='mb-4'>
      <AlertFloating title='Error' description={coercionError} color='error' />
    </div>
  ) : null;

  if (subStep === 'summary') {
    const summaryProps = {
      importData,
      orgId,
      onApprove: handleApprove,
      onResolveConflicts: handleResolveConflicts,
      onEditMappings,
      isApproving,
      onReviewCoercions:
        invalidRowCount > 0 ? handleReviewCoercions : undefined,
      coercionCtaLabel: hasStoredCoercions
        ? 'Review suggested fixes'
        : 'Let our agent fix these',
      isReviewingCoercions: isSuggesting,
    };
    return (
      <>
        {approveErrorBanner}
        {coercionErrorBanner}
        {coercionEntryBanner}
        {isLargeImport ? (
          <LargeImportSummary {...summaryProps} />
        ) : (
          <SummaryView {...summaryProps} />
        )}
      </>
    );
  }

  if (subStep === 'coercions' && importId) {
    return isLargeImport ? (
      <LargeImportCoercionReview
        importData={importData}
        orgId={orgId}
        importId={importId}
        onBack={handleBackToSummary}
        onApplied={handleCoercionsApplied}
      />
    ) : (
      <CoercionReview
        importData={importData}
        orgId={orgId}
        importId={importId}
        onBack={handleBackToSummary}
        onApplied={handleCoercionsApplied}
      />
    );
  }

  if (subStep === 'conflicts') {
    return (
      <>
        {approveErrorBanner}
        <ConflictResolution
          importData={importData}
          resolutions={conflictResolutions}
          onResolutionsChange={setConflictResolutions}
          onBack={handleBackToSummary}
          onApprove={handleApprove}
          isApproving={isApproving}
          orgId={orgId}
        />
      </>
    );
  }

  return null;
}
